import axios from 'axios';
import { getApiUrl } from '../config/api';

interface Project {
  id: string;
  title: string;
  description?: string; // Add description field
  type: string;
  duration: string;
  thumbnail: string;
  createdAt: string;
  metadata?: any;
  date?: string;
  originalVideoUrl?: string;
  captionedVideoUrl?: string;
  generatedVideoUrl?: string;
  soundTrackVideoUrl?: string; // 🆕 NEW: Add soundtrack video URL
  originalAudioUrl?: string;
  finalAudio?: string;
  hasCaptions?: boolean;
  stage?: string;
  status?: string;
  // Video segmentation fields
  isVideoSegmented?: boolean;
  totalVideoSegments?: number;
  originalVideoDuration?: number;
}

interface PromptProject extends Project {
  promptData?: {
    generatedPrompt: string;
    userSelections: {
      userIdea: string;
      attributes: string;
    };
  };
}

interface ScriptProject extends Project {
  scriptData?: {
    generatedScript: string;
    userSelections: {
      [key: string]: any;
    };
  };
}

class ProjectService {
  private getBaseUrl() {
    return getApiUrl('').replace(/\/$/, '');
  }

  private getAuthHeaders() {
    const token = localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
    };
  }

  async getProjects(): Promise<Project[]> {
    try {
      const response = await axios.get(`${this.getBaseUrl()}/projects`, {
        headers: this.getAuthHeaders()
      });
      return response.data.projects || response.data;
    } catch (error) {
      console.error('Error fetching projects:', error);
      return [];
    }
  }

  async getPromptProjectsByProjectId(projectId: string): Promise<PromptProject[]> {
    try {
      console.log(`🔍 Loading all prompts for specific project: ${projectId}`);
      
      // Use the prompt-generator endpoint to get all prompts for this project
      const response = await fetch(getApiUrl(`/prompt-generator/prompts/${projectId}`), {
        headers: this.getAuthHeaders()
      });
      
      if (!response.ok) {
        console.log('❌ Failed to fetch prompts for project:', projectId);
        return [];
      }
      
      const result = await response.json();
      if (!result.success || !result.data || result.data.length === 0) {
        console.log('❌ No prompts found for project:', projectId);
        return [];
      }
      
      console.log('✅ Found prompts for project:', projectId, 'Count:', result.data.length);
      console.log('🔍 Sample prompt data structure:', result.data[0]);
      
      // Transform the prompt data into PromptProject format
      const prompts: PromptProject[] = result.data.map((prompt: any) => {
        console.log('🔍 Processing prompt:', prompt.id, 'promptMetadata:', prompt.promptMetadata);
        
        return {
          id: prompt.id, // Use unique prompt ID instead of project ID
          title: prompt.title || 'Untitled Prompt',
          description: `Prompt created on ${new Date(prompt.createdAt).toLocaleDateString()}`,
          createdAt: prompt.createdAt,
          hasPrompt: true,
          type: 'prompt',
          metadata: prompt.project?.metadata, // Include project metadata for character registry access
          promptData: {
            id: prompt.id,
            generatedPrompt: prompt.generatedPrompt,
            userSelections: prompt.userSelections,
            promptMetadata: prompt.promptMetadata, // Add promptMetadata to access characterId
            title: prompt.title,
            createdAt: prompt.createdAt,
            updatedAt: prompt.updatedAt
          }
        };
      });
      
      return prompts;
    } catch (error) {
      console.error('❌ Error loading prompts for project:', projectId, error);
      return [];
    }
  }

  async getPromptProjects(): Promise<PromptProject[]> {
    try {
      console.log('🔍 Loading all projects and filtering for hasPrompt=true...');
      
      // Load ALL projects like other components do
      const response = await fetch(`${this.getBaseUrl()}/projects?page=1&limit=1000`, {
        headers: this.getAuthHeaders()
      });
      if (!response.ok) {
        throw new Error('Failed to fetch projects');
      }
      
      const result = await response.json();
      const allProjects = result.projects || [];
      console.log('📦 Loaded all projects:', allProjects.length);
      
      // Filter for projects that have hasPrompt = true
      const projectsWithPrompts = allProjects.filter((project: any) => project.hasPrompt === true);
      console.log('🎯 Projects with hasPrompt=true:', projectsWithPrompts.length, projectsWithPrompts);
      
      // Fetch prompt data for each project that has hasPrompt=true
      const projectsWithPromptData = await Promise.all(
        projectsWithPrompts.map(async (project: Project) => {
          try {
            console.log(`🔄 Fetching prompt data for project: ${project.id}`);
            const promptResponse = await fetch(`${this.getBaseUrl()}/prompt-generator/project/${project.id}`, {
              headers: this.getAuthHeaders()
            });
            if (promptResponse.ok) {
              const promptResult = await promptResponse.json();
              if (promptResult.success && promptResult.data) {
                console.log('✅ Found prompt data for project:', project.id);
                return { ...project, promptData: promptResult.data };
              }
            }
            console.log('❌ No prompt data found for project:', project.id);
            return { ...project, promptData: null };
          } catch (error) {
            console.error(`❌ Error fetching prompt data for project ${project.id}:`, error);
            return { ...project, promptData: null };
          }
        })
      );
      
      // Filter out projects without actual prompt data
      const finalProjects = projectsWithPromptData.filter(project => project.promptData !== null);
      console.log('🎉 Final projects with complete prompt data:', finalProjects.length, finalProjects);
      return finalProjects;
    } catch (error) {
      console.error('❌ Error loading prompt projects:', error);
      return [];
    }
  }

  async getProject(projectId: string): Promise<PromptProject | null> {
    try {
      console.log(`🔍 Fetching project: ${projectId}`);
      const headers = this.getAuthHeaders();
      console.log('🔑 Auth headers for getProject:', { 
        hasContentType: !!headers['Content-Type'],
        hasAuth: !!headers['Authorization'],
        authPreview: headers['Authorization'] ? headers['Authorization'].substring(0, 20) + '...' : 'None'
      });
      
      const response = await axios.get(`${this.getBaseUrl()}/projects/${projectId}`, {
        headers
      });
      
      console.log(`📥 getProject response: ${response.status} ${response.statusText}`);
      const project = response.data;
      
      // Fetch prompt data if the project has prompts
      if (project.hasPrompt) {
        try {
          const promptResponse = await axios.get(`${this.getBaseUrl()}/prompt-generator/project/${projectId}`, {
            headers: this.getAuthHeaders()
          });
          if (promptResponse.data.success && promptResponse.data.data) {
            return { ...project, promptData: promptResponse.data.data };
          }
        } catch (error) {
          console.error(`Error fetching prompt data for project ${projectId}:`, error);
        }
      }
      
      return project;
    } catch (error) {
      console.error('Error fetching project:', error);
      return null;
    }
  }

  async updateProjectTitle(projectId: string, title: string): Promise<boolean> {
    try {
      const response = await axios.put(`${this.getBaseUrl()}/projects/${projectId}`, 
        { title }, 
        { headers: this.getAuthHeaders() }
      );
      return response.status === 200;
    } catch (error) {
      console.error('Error updating project title:', error);
      return false;
    }
  }
}

export const projectService = new ProjectService();
export type { Project, PromptProject, ScriptProject };

// Re-export ProjectStage enum that components expect
export enum ProjectStage {
  CONTENT_APP = 'CONTENT_APP',
  SCRIPT_GENERATION = 'SCRIPT_GENERATION',
  CAPTION = 'CAPTION',
  TITLE_GENERATION = 'TITLE_GENERATION',
  THUMBNAIL_GENERATION = 'THUMBNAIL_GENERATION',
  PROMPT_GENERATION = 'PROMPT_GENERATION',
  EDIT = 'EDIT',
  CONTENT = 'CONTENT'
}

// Legacy function exports that existing components depend on
export const fetchProjects = async (page: number = 1, limit: number = 20) => {
  const token = localStorage.getItem('authToken');
  
  console.log('🔑 Auth token for fetchProjects:', token ? 'Token present' : 'No token found');
  
  const headers = {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
  };
  
  console.log('📤 fetchProjects headers:', headers);
  
  const response = await fetch(getApiUrl(`/projects?page=${page}&limit=${limit}`), {
    headers
  });
  
  console.log('📥 fetchProjects response status:', response.status);
  
  if (!response.ok) {
    const errorText = await response.text();
    console.error('❌ fetchProjects error response:', errorText);
    throw new Error(`Failed to fetch projects: ${response.status} ${response.statusText}`);
  }
  return await response.json();
};
export const deleteProject = async (id: string) => {
  const token = localStorage.getItem('authToken');
  const headers = {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
  };
  
  const response = await fetch(getApiUrl(`/projects/${id}`), { 
    method: 'DELETE',
    headers
  });
  return response.ok;
};

export const duplicateProject = async (id: string) => {
  const token = localStorage.getItem('authToken');
  const headers = {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
  };
  
  const response = await fetch(getApiUrl(`/projects/${id}/duplicate`), { 
    method: 'POST',
    headers
  });
  return response.ok ? await response.json() : null;
};

export const updateProjectStage = async (id: string, stage: string) => {
  const token = localStorage.getItem('authToken');
  const headers = {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
  };
  
  const response = await fetch(getApiUrl(`/projects/${id}/stage`), {
    method: 'PUT',
    headers,
    body: JSON.stringify({ stage })
  });
  return response.ok;
};

// Get a single project by ID
export const getProject = async (id: string): Promise<Project> => {
  const token = localStorage.getItem('authToken');
  const headers = {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
  };
  
  const response = await fetch(getApiUrl(`/projects/${id}`), {
    method: 'GET',
    headers
  });
  
  if (!response.ok) {
    throw new Error(`Failed to fetch project: ${response.status} ${response.statusText}`);
  }
  
  return await response.json();
};

// Update a project with new data
export const updateProject = async (id: string, updateData: Partial<Project>): Promise<Project> => {
  console.log('🚀 projectService.updateProject called with:', {
    id,
    updateData: JSON.stringify(updateData, null, 2)
  });
  
  const token = localStorage.getItem('authToken');
  const headers = {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
  };
  
  console.log('🔑 PUT request headers:', headers);
  console.log('🌐 PUT request URL:', getApiUrl(`/projects/${id}`));
  console.log('📦 PUT request body:', JSON.stringify(updateData, null, 2));
  
  const response = await fetch(getApiUrl(`/projects/${id}`), {
    method: 'PUT',
    headers,
    body: JSON.stringify(updateData)
  });
  
  console.log('📥 PUT response status:', response.status, response.statusText);
  
  if (!response.ok) {
    const errorText = await response.text();
    console.error('❌ PUT request failed:', errorText);
    throw new Error(`Failed to update project: ${response.status} ${response.statusText}`);
  }
  
  const responseData = await response.json();
  console.log('✅ PUT request successful, response data:', responseData);
  
  return responseData;
};

// Utility function to generate title from prompt
export const generateTitleFromPrompt = (prompt: string, maxWords: number = 30): string => {
  if (!prompt) return 'Untitled Prompt';
  
  // Clean the prompt - remove extra whitespace and newlines
  const cleanPrompt = prompt.replace(/\s+/g, ' ').trim();
  
  // Split into words and take first maxWords
  const words = cleanPrompt.split(' ').slice(0, maxWords);
  
  // Join back and add ellipsis if truncated
  let title = words.join(' ');
  if (cleanPrompt.split(' ').length > maxWords) {
    title += '...';
  }
  
  return title;
};
